import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connectRefinementList } from 'react-instantsearch-dom';
import classNames from 'classnames';

import Check from '../../icons/checkbox-marker.svg';
import Arrow from '../../icons/dropdownArrow.svg';
import { CustomRefinementItem, CustomRefinementListProps } from './models';

import './CustomRefinementList.scss';

const RefinementList = ({
  items,
  refine,
  id,
  handleCheckedChange,
  selectedFiltersText,
  filterText,
  filterType,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<CustomRefinementItem[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const optionsRef = useRef<HTMLInputElement[]>([]);

  const transformItems = useMemo(
    () =>
      filterType === 'year'
        ? items.sort((a, b) => {
            const labelA = a.label.toUpperCase();
            const labelB = b.label.toUpperCase();

            return labelA === labelB ? 0 : labelA > labelB ? -1 : 1;
          })
        : items.sort((a, b) => {
            const labelA = a.label.toUpperCase();
            const labelB = b.label.toUpperCase();

            return labelA === labelB ? 0 : labelA > labelB ? 1 : -1;
          }),
    [items, filterType]
  );

  const onChangeState = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
      optionsRef.current[0]?.focus();
    }
  }, [isOpen, optionsRef.current, transformItems]);

  const onRefineItem = useCallback(
    (value) => {
      refine(value);
      setSelected(items.filter((item) => item === items.isRefined));
      handleCheckedChange();
    },
    [items]
  );

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    setSelected(items.filter((item) => item.isRefined === true));
  }, [items]);

  const handleKeyDown = useCallback(
    (e) => {
      if (!e.shiftKey && e.keyCode === 9) {
        if (
          isOpen &&
          dropdownRef.current &&
          optionsRef.current &&
          optionsRef.current[optionsRef.current.length - 1] === e.target
        ) {
          setIsOpen(false);
        }
      }

      if (e.shiftKey && e.keyCode === 9) {
        if (isOpen && dropdownRef.current && btnRef.current === e.target) {
          setIsOpen(false);
        }
      }

      if (e.keyCode === 40) {
        e.preventDefault();
        if (
          isOpen &&
          dropdownRef.current &&
          optionsRef.current &&
          e.target.parentNode.nextSibling
        ) {
          e.target.parentNode.nextSibling.firstChild.focus();
        }
      }

      if (e.keyCode === 38) {
        e.preventDefault();
        if (isOpen && dropdownRef.current && e.target.parentNode.previousSibling) {
          e.target.parentNode.previousSibling.firstChild.focus();
        }
      }
    },
    [isOpen, dropdownRef.current, optionsRef.current, transformItems]
  );

  return (
    <div
      className={classNames('filters__refinement', {
        'filters__refinement--active': isOpen,
      })}
      ref={dropdownRef}
      data-id={id}
      role="presentation"
      onKeyDown={handleKeyDown}
    >
      <div className="filters__refinement-dropdown">
        <button
          className="filters__refinement-btn"
          aria-expanded={isOpen}
          type="button"
          ref={btnRef}
          onClick={() => {
            onChangeState();
          }}
        >
          <span className="breadcrumb">
            {selected.length > 0 ? `${selected.length} ${selectedFiltersText}` : filterText}
          </span>
          <span className="filters__refinement-icon" aria-hidden="true">
            <Arrow className="filters__refinement-pic" />
          </span>
        </button>
        <ul
          tabIndex={-1}
          className={classNames('filters__refinement-list', {
            'filters__refinement-list--active': isOpen,
          })}
        >
          {transformItems?.map((item, i) => (
            <li key={item.label} className="filters__refinement-item" tabIndex={-1}>
              <input
                id={item.label}
                type="checkbox"
                tabIndex={isOpen ? 0 : -1}
                onChange={() => {
                  onRefineItem(item.value);
                }}
                data-filter-type={filterType}
                className={classNames('filters__refinement-checkbox', {
                  'filters__refinement-checkbox--checked': item.isRefined,
                })}
                ref={(checkbox: HTMLInputElement) => {
                  optionsRef.current[i] = checkbox;
                }}
              />
              <span className="filters__refinement-marker" aria-hidden="true">
                <Check className="filters__refinement-marker-icon" />
              </span>
              <label className="filters__refinement-label breadcrumb" htmlFor={item.label}>
                {item.label}
              </label>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const CustomList = connectRefinementList(RefinementList);

const CustomRefinementList: FC<CustomRefinementListProps> = ({
  filter,
  id,
  handleCheckedChange,
  selectedFiltersText,
  filterText,
}) => (
  <CustomList
    attribute={filter}
    id={id}
    limit={100}
    handleCheckedChange={handleCheckedChange}
    selectedFiltersText={selectedFiltersText}
    filterText={filterText}
    filterType={filter}
  />
);

export default CustomRefinementList;
